import { Link } from "react-router-dom";
import { Github, Twitter, Linkedin } from "lucide-react";
import { Logo } from "@/components/ui/logo";
import { useUptimeStatus } from "@/hooks/useUptimeStatus";

const footerLinks = {
  product: [
    { label: "Guided demo", href: "/demo/live-order-tracking" },
    { label: "Workflow", href: "/home#workflow" },
    { label: "Features", href: "/home#features" },
    { label: "Pricing", href: "/pricing" },
    { label: "Changelog", href: "/changelog" },
  ],
  solutions: [
    { label: "Product managers", href: "/for/product-managers" },
    { label: "Startups", href: "/for/startups" },
    { label: "Product + engineering", href: "/for/product-engineering-teams" },
  ],
  resources: [
    { label: "Documentation", href: "/docs" },
    { label: "Blog", href: "/blog" },
    { label: "Guides", href: "/docs" },
  ],
  company: [
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact-sales" },
    { label: "Security", href: "/security" },
  ],
  legal: [
    { label: "Privacy", href: "/privacy" },
    { label: "Terms", href: "/terms" },
    { label: "Security", href: "/security" },
    { label: "DPA", href: "/dpa" },
  ],
};

const socialLinks = [
  { icon: Github, href: "https://github.com/ReqqyApp", label: "GitHub" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/reqqy", label: "LinkedIn" },
];

export function Footer() {
  const { status, label } = useUptimeStatus();

  const statusDotClass =
    status === "operational"
      ? "status-dot status-complete"
      : status === "degraded"
        ? "status-dot status-running"
        : status === "down"
          ? "status-dot status-failed"
          : "status-dot";

  return (
    <footer className="border-t border-border bg-card/50">
      <div className="container px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-8 md:gap-10">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <Logo variant="full" className="h-8 w-auto" />
            </Link>
            <p className="text-sm text-muted-foreground max-w-xs mb-6">
              Requirements intelligence for product teams: context, generation, review, refinement, and export before engineering starts.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <p className="font-semibold text-sm mb-4">Product</p>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-semibold text-sm mb-4">Solutions</p>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-semibold text-sm mb-4">Resources</p>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-semibold text-sm mb-4">Company</p>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-semibold text-sm mb-4">Legal</p>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-border/60 bg-background/70 p-4 text-xs leading-5 text-muted-foreground">
          <span className="font-medium text-foreground">Early-access notice:</span> Reqqy is currently in beta. AI-assisted outputs may be incomplete or inaccurate and should be reviewed before use. Please avoid uploading sensitive, regulated, or production-confidential information unless you have confirmed Reqqy is suitable for that use case. Use of Reqqy is subject to the <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">Terms</Link>, <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>, and <Link to="/security" className="underline underline-offset-2 hover:text-foreground">Security overview</Link>.
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} Reqqy. All rights reserved.</p>
          <Link to="https://stats.uptimerobot.com/ocGpPsaCEB">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className={statusDotClass}></span>
              {label}
            </div>
          </Link>
        </div>
      </div>
    </footer>
  );
}
